import { CaseSlaStatus } from '@compass/client/types/cases_analytics';
import { useOrgAndPrograms } from '@hooks/useOrgAndPrograms';
import { ShieldCheck, XCircle } from 'lucide-react';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/EmptyState';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { CASES_ANALYTICS_SAVED_VIEWS_CONFIG } from '@/features/savedViews/config';
import { SavedViewsMenu } from '@/features/savedViews/SavedViewsMenu';
import { useDownloadChart } from '@/pages/analytics/hooks/useDownloadChart';
import { resolveAnalyticsOrganizationId } from '@/pages/analytics/utils/resolveOrg';
import { useStore } from '@/store';
import { removeOrgPrefix } from '@/utils/routes';

import { BreachedSummaryStrip } from './components/BreachedSummaryStrip';
import { CaseBreakdownSection } from './components/CaseBreakdownSection';
import { CasesAnalyticsSkeleton } from './components/CasesAnalyticsSkeleton';
import { CasesFilters } from './components/CasesFilters';
import { CasesKpiGrid } from './components/CasesKpiGrid';
import { SlaComplianceCard } from './components/SlaComplianceCard';
import { StepTimeCard } from './components/StepTimeCard';
import { SubmittedTrendCard } from './components/SubmittedTrendCard';
import { TimeToSubmitCard } from './components/TimeToSubmitCard';
import { useCasesAnalytics } from './hooks/useCasesAnalytics';
import { useCasesAnalyticsFiltersState } from './hooks/useCasesAnalyticsFiltersState';
import { buildGroupColors } from './utils/groupColors';

export const CasesAnalyticsContainer: React.FC = () => {
  const navigate = useNavigate();
  const { orgId } = useParams<{ orgId?: string }>();
  const {
    organizations,
    programs,
    selectedOrganizationId,
    isLoadingOrgAndPrograms,
    unauthorizedAccess,
  } = useOrgAndPrograms();
  const timeZone = useStore(state => state.getTimeZone());
  const organizationId = resolveAnalyticsOrganizationId(
    orgId ? removeOrgPrefix(orgId) : null,
    selectedOrganizationId,
  );

  const {
    programIds,
    setProgramIds,
    reportTypes,
    setReportTypes,
    slaStatus,
    setSlaStatus,
    start,
    end,
    setDateRange,
  } = useCasesAnalyticsFiltersState({ timeZone });

  const query = useCasesAnalytics({
    organizationId,
    programIds,
    reportTypes,
    slaStatus,
    start,
    end,
    timeZone,
  });
  const data = query.data;
  const download = useDownloadChart();
  const trendBucket = query.trendBucket;
  const bucketDates = useMemo(() => Object.values(query.labelByStart), [query.labelByStart]);
  const colors = useMemo(() => buildGroupColors(data), [data]);
  // The query is gated on start/end; treat that window as loading so the page shows skeletons
  // instead of flashing empty states.
  const loading = query.isLoading || !start || !end;

  const programOptions = useMemo(
    () =>
      programs
        .filter(p => !!p.id)
        .map(p => ({ label: p.name || p.id || '', value: p.id! }))
        .sort((l, r) => l.label.localeCompare(r.label)),
    [programs],
  );
  // Resolved so the org name is available to the PDF/CSV exports added with the charts.
  void organizations;

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>AE / PQC Analytics</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (unauthorizedAccess?.isUnauthorized) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="Access Denied"
          description="You do not have access to this organization. Please contact your administrator if you believe this is an error."
          icon={XCircle}
          iconClassName="text-muted-foreground"
          action={{ label: 'Go to Home', onClick: () => navigate('/') }}
        />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="flex h-full flex-col">
        <header className="flex items-start gap-3 px-6 pt-6">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
            <ShieldCheck className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-foreground">AE / PQC Analytics</h1>
            <p className="text-sm text-muted-foreground">
              Adverse-event and product-quality-complaint reporting performance.
            </p>
          </div>
        </header>

        <div className="flex-1 overflow-auto px-6 py-6">
          {!organizationId || isLoadingOrgAndPrograms ? (
            <CasesAnalyticsSkeleton />
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <CasesFilters
                  programOptions={programOptions}
                  selectedProgramIds={programIds}
                  onProgramFilterChange={setProgramIds}
                  reportTypes={reportTypes}
                  onReportTypesChange={setReportTypes}
                  slaStatus={slaStatus}
                  onSlaStatusChange={setSlaStatus}
                  summary={data?.summary}
                  start={start}
                  end={end}
                  timeZone={timeZone}
                  onDateRangeChange={setDateRange}
                />
                <SavedViewsMenu
                  config={CASES_ANALYTICS_SAVED_VIEWS_CONFIG}
                  organizationId={organizationId}
                />
              </div>

              {query.error ? (
                <EmptyState
                  title="Couldn't load AE / PQC analytics"
                  description={
                    query.error instanceof Error
                      ? query.error.message
                      : 'The analytics request failed. Please try again.'
                  }
                  icon={XCircle}
                  action={{ label: 'Retry', onClick: () => query.refetch() }}
                />
              ) : (
                <>
                  <CasesKpiGrid summary={data?.summary} loading={loading} />
                  {slaStatus === CaseSlaStatus.CASE_SLA_STATUS_BREACHED && (
                    <BreachedSummaryStrip
                      summary={data?.breached_summary}
                      breachedCases={data?.summary?.breached_cases?.value ?? 0}
                      loading={loading}
                    />
                  )}

                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <SubmittedTrendCard
                      data={data?.submitted_trend}
                      trendBucket={trendBucket}
                      bucketDates={bucketDates}
                      partialDates={query.partialDates}
                      colors={colors}
                      download={download}
                      loading={loading}
                    />
                    <SlaComplianceCard
                      data={data?.sla_compliance_trend}
                      trendBucket={trendBucket}
                      partialDates={query.partialDates}
                      colors={colors}
                      download={download}
                      loading={loading}
                    />
                  </div>
                  <CaseBreakdownSection
                    breakdown={data?.breakdown}
                    colors={colors}
                    download={download}
                    loading={loading}
                  />
                  <TimeToSubmitCard
                    data={data?.time_to_submit}
                    colors={colors}
                    download={download}
                    loading={loading}
                  />
                  <StepTimeCard
                    data={data?.time_by_step}
                    trendBucket={trendBucket}
                    bucketDates={bucketDates}
                    partialDates={query.partialDates}
                    download={download}
                    loading={loading}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
